import { useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import swal from "sweetalert";

import CustomTip from "../../common/CustomToolTip/CustomTip";
import ViewSingleSubmittedAggregator from "./ViewSingleSubmittedAggregator";
import CreateMasterMerchant from "./CreateMasterMerchant";
import MasterMerchantEdit from "./MasterMerchantEdit";

import { MasterMerchantActionRegistry } from "../../Privillages/ActionRegistry";
import useHasPrivilege from "../../Privillages/useHasPrivilege";
import useApiServices from "../../utils/useApiServices";
import { masterMerchantService } from "../../Services/masterMerchant.service";
import { useMasterMerchants } from "../../hooks/useMasterMerchants";

/* ===========================
   ROW ACTION MENU
=========================== */
const ActionMenu = ({ row, onAction, canEdit }) => {
  const [value, setValue] = useState("");

  const handleChange = (e) => {
    const action = e.target.value;
    setValue("");
    onAction(row, action);
  };

  return (
    <Select
      value={value}
      displayEmpty
      renderValue={() => null}
      onChange={handleChange}
      sx={{
        width: 40,
        backgroundColor: "#A3C8ED",
        borderRadius: 2,
        height: 25,
      }}
    >
      <MenuItem value="view">View</MenuItem>

      {canEdit && <MenuItem value="edit">Edit</MenuItem>}

      {canEdit &&
        (row.active ? (
          <MenuItem value="disable" className="bg-danger text-white">
            Disable
          </MenuItem>
        ) : (
          <MenuItem value="enable" className="bg-success text-white">
            Enable
          </MenuItem>
        ))}
    </Select>
  );
};

/* ===========================
   MAIN COMPONENT
=========================== */
const MMSubmitted = () => {
  const { SECTION_KEY, MASTER_CREATE, MATSER_EDIT } =
    MasterMerchantActionRegistry;

  const hasMasterCreate = useHasPrivilege(SECTION_KEY, MASTER_CREATE);
  const hasMasterEdit = useHasPrivilege(SECTION_KEY, MATSER_EDIT);

  const api = useApiServices();
  const service = masterMerchantService(api);

  const { data, loading, refetch } = useMasterMerchants();

  const [currentId, setCurrentId] = useState(null);
  const [showView, setShowView] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  /* ===========================
     ACTION HANDLER
  =========================== */
  const handleAction = async (row, action) => {
    switch (action) {
      case "view":
        setCurrentId(row.masterMerchantId);
        setShowView(true);
        break;

      case "edit":
        setCurrentId(row.masterMerchantId);
        setShowEdit(true);
        break;

      case "enable":
      case "disable": {
        const label = action === "enable" ? "Enable" : "Disable";

        const confirm = await swal({
          title: "Alert",
          text: `Are you sure you want to ${label} this Aggregator?`,
          icon: "warning",
          buttons: true,
          dangerMode: true,
        });

        if (!confirm) return;

        const res = await service.toggleStatus(row.masterMerchantId, action);

        if (res?.statusCode === 200) {
          swal("Success", `Aggregator ${label}d Successfully`, "success");
          refetch();
        } else {
          swal("Error", `Failed to ${label} Aggregator`, "error");
        }
        break;
      }

      default:
        break;
    }
  };

  /* ===========================
     TABLE COLUMNS
  =========================== */
  const columns = [
    {
      field: "name",
      headerName: "Master Merchant Name",
      minWidth: 300,
    },
    {
      field: "midprifix",
      headerName: "Prefix",
      minWidth: 120,
    },
    {
      field: "email",
      headerName: "e-Mail",
      minWidth: 260,
    },
    {
      field: "mobile",
      headerName: "Mobile Number",
      minWidth: 200,
    },
    {
      field: "action",
      headerName: "Action",
      minWidth: 90,
      sortable: false,
      renderCell: ({ row }) => (
        <ActionMenu row={row} canEdit={hasMasterEdit} onAction={handleAction} />
      ),
    },
  ];

  return (
    <div className="d-flex justify-content-end">
      <div className="w-75 m-4">
        <div className="container-fluid">
          {/* Header */}
          <div className="my-4 mx-3 d-flex justify-content-between align-items-center">
            <div className="fs-2 fw-semibold text-black">Master Merchant</div>

            {hasMasterCreate && (
              <CustomTip title="Create Master Merchant">
                <div
                  className="icon-container px-2 mb-3 me-3"
                  onClick={() => setShowCreate(true)}
                >
                  <PersonAddAlt1Icon fontSize="large" />
                </div>
              </CustomTip>
            )}
          </div>

          {/* Table */}
          <div className="card">
            <div className="card-body">
              <DataGrid
                rows={data || []}
                columns={columns}
                loading={loading}
                autoHeight
                disableRowSelectionOnClick
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: { showQuickFilter: true },
                }}
              />
            </div>
          </div>
        </div>

        {/* Modals */}
        <ViewSingleSubmittedAggregator
          show={showView}
          id={currentId}
          onHide={() => setShowView(false)}
        />

        <CreateMasterMerchant
          show={showCreate}
          onHide={() => setShowCreate(false)}
          onSuccess={refetch}
        />

        <MasterMerchantEdit
          show={showEdit}
          id={currentId}
          onHide={() => setShowEdit(false)}
          onSuccess={refetch}
        />
      </div>
    </div>
  );
};

export default MMSubmitted;